import { ICellRendererParams } from "ag-grid-community";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ExternalLinkIcon } from "@heroicons/react/outline";

import type { RequestRow } from "../requestLogGridTypes";

import { HydrationPendingCell, isRequestRowHydrating } from "./HydrationPendingCell";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useUser } from "@/context/user-context";
import { useEvaluateTagReports } from "@/hooks/evaluate/useEvaluateTagReports";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { processTag, sortTagsWithLinksFirst } from "@/utils/tagUtils";

export const TagsCellRenderer = ({ data }: ICellRendererParams<RequestRow>) => {
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;
  const isHydrating = isRequestRowHydrating(data);
  const tags = useMemo(() => {
    if (Array.isArray(data?.tags_array) && data.tags_array.length > 0) {
      return data.tags_array;
    }

    if (isHydrating) {
      return [];
    }

    if (Array.isArray(data?.tags) && data.tags.length > 0) {
      return data.tags;
    }

    return [];
  }, [data?.tags_array, data?.tags, isHydrating]);
  const { metadataMap: evaluateMetadataMap } = useEvaluateTagReports(tags || []);

  const sortedTags = useMemo(() => {
    if (tags.length === 0) {
      return [];
    }
    return sortTagsWithLinksFirst(tags);
  }, [tags]);
  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: sortedTags.length,
    keepMinOne: true,
  });

  if (isHydrating && sortedTags.length === 0) {
    return <HydrationPendingCell />;
  }

  if (sortedTags.length === 0) {
    return <span className="text-gray-400">—</span>;
  }

  const renderTag = (tag: string) => {
    const { isLink, displayText, linkPath } = processTag(
      tag,
      workspaceId?.toString() || undefined,
      evaluateMetadataMap,
    );
    const tagElement = (
      <span
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ${
          isLink
            ? "border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
            : "bg-gray-200 text-gray-800"
        }`}
      >
        {isLink && <ExternalLinkIcon className="h-3 w-3 flex-shrink-0" />}
        {displayText}
      </span>
    );
    return linkPath ? (
      <Link key={tag} to={linkPath} onClick={e => e.stopPropagation()} className="no-underline">
        {tagElement}
      </Link>
    ) : (
      <span key={tag}>{tagElement}</span>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative flex h-full w-full min-w-0 flex-1 items-center gap-1"
    >
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute flex w-full gap-1"
        aria-hidden="true"
      >
        {sortedTags.map(tag => renderTag(tag))}
      </div>
      {sortedTags.slice(0, visibleCount).map(tag => renderTag(tag))}
      {hiddenCount > 0 && (
        <BasicTooltip content={sortedTags.slice(visibleCount).join(", ")}>
          <span>
            <Badge variant={BadgeVariant.GRAY} size={BadgeSize.SM}>
              +{hiddenCount}
            </Badge>
          </span>
        </BasicTooltip>
      )}
    </div>
  );
};
